import { FC, useCallback, useContext } from 'react';
import { Registration } from '@stax-core/types-omni';
import { Checkbox, Field } from '../../components';
import { MainForm } from '../components/application/MainForm';
import { BackButton } from '../components/BackButton';
import { CenteredContent } from '../components/CenteredContent';
import { NextButton } from '../components/NextButton';
import { signupReasons } from '../constants/signupReasons';
import { coreapi } from '../../api';
import { useToaster, useWriteableFieldFormValidation } from '../../hooks';
import {
  AuthStore,
  BrandStore,
  RegistrationStore,
  updateRegistration,
} from '../../context';

interface FieldValues
  extends Pick<Registration, 'pricing_plan' | 'chosen_processing_method'> {
  meta: Extract<Registration, 'meta'> & {
    processing_methods: string[];
  };
}

export const SignupReasonPage: FC = () => {
  const {
    state: { brand },
  } = useContext(BrandStore);
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const { toaster, toast } = useToaster();
  const {
    state: { auth },
  } = useContext(AuthStore);
  const authToken = auth?.token ?? null;

  const validate = useWriteableFieldFormValidation((values: FieldValues) => {
    const errors: Partial<Record<keyof FieldValues, any>> = {};
    if (!values.chosen_processing_method) {
      errors.chosen_processing_method = 'Please select a processing method';
    }
    return errors;
  });

  const onSubmit = useCallback(
    async (formValues: FieldValues) => {
      // if this merchant already has a pricing_plan set, use it
      // otherwise, find an appropriate pricing_plan based off of the chosen_processing_method
      const pricingPlan = formValues.pricing_plan
        ? formValues.pricing_plan
        : formValues.chosen_processing_method === 'in-person'
        ? 'interchange0-15'
        : 'interchange0-18';
      const payload = {
        chosen_processing_method: formValues.chosen_processing_method,
        pricing_plan: pricingPlan,
      };

      try {
        const r = await coreapi.put(authToken!, '/team/registration', payload);
        dispatch(updateRegistration(r));
        return true;
      } catch (error) {
        toaster(toast.error(error, 'There was a problem saving your changes.'));
      }
    },
    [authToken, dispatch, toast, toaster],
  );

  return (
    <MainForm<FieldValues>
      initialValues={{
        chosen_processing_method: registration?.chosen_processing_method,
      }}
      validate={validate}
      onSubmit={onSubmit}
    >
      {formProps => (
        <CenteredContent
          id="application-signup-reason"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 100 }}
          exit={{ opacity: 0, y: -20 }}
        >
          <BackButton id="application-signup-reason-back" />
          <h2>How will you primarily use {brand?.platformName || 'Stax'}?</h2>
          <div>
            <p>
              This is just to get you started. You can always do more later.
            </p>
            {signupReasons.map(reason => (
              <div
                style={{ margin: '16px 0' }}
                data-testid={`application-signup-reason-checkbox-${reason.key}`}
              >
                <Field
                  name={`chosen_processing_method`}
                  label={reason.label}
                  type="radio"
                  value={reason.key}
                  checked={
                    formProps.values.chosen_processing_method === reason.key
                  }
                >
                  {props => (
                    <>
                      <Checkbox
                        {...props.input}
                        id={`chosen_processing_method-${reason.key}`}
                        variant={'secondary'}
                      />
                    </>
                  )}
                </Field>
              </div>
            ))}
          </div>
          <NextButton
            id="application-signup-reason-continue"
            formProps={formProps}
          />
        </CenteredContent>
      )}
    </MainForm>
  );
};
